/**
 * The stories this practice is following, and the ones that ended.
 *
 * A thread is a wikilink somebody promoted: the tag plus a title, a framing, a
 * lifecycle and a timeline. Two views behind one file, because they are one
 * idea and share every type — the list at `/threads`, one thread at
 * `/threads/:id`.
 *
 * A top-level page rather than an Admin tab, for the reason the Judgments page
 * gives: deciding what the practice follows is an editorial act, not an
 * administrative one, and it is recurring work that a locked door discourages.
 *
 * Neither view starts an interval or a stream, so `registerPageCleanup` is
 * deliberately not used here. That absence is a decision, not an oversight.
 */
import { api, ApiError } from "../lib/api";
import { renderHeader } from "../components/header";
import { navigate } from "../lib/router";
import { fmtDate } from "../lib/datetime";

// ---------------------------------------------------------------------------
// Types — see backend/routers/threads.py
// ---------------------------------------------------------------------------

export interface Thread {
  id: string;
  wikilinkId: string | null;
  /** Null once a merge folded this thread into another. */
  wikilink: string | null;
  title: string;
  framing: string | null;
  status: "open" | "closed";
  signalCount: number;
  firstSeen: string | null;
  lastActivity: string | null;
  dormant: boolean;
  dormantAfterDays: number;
  mergedIntoId: string | null;
  openedBy: string | null;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
  closingNote: string | null;
}

interface TimelineEntry {
  id: string;
  signalRef: string | null;
  title: string;
  source: string;
  publisherDomain: string | null;
  sourceUrl: string;
  datePublished: string;
  summary: string;
  horizon: string | null;
  watchArea: string | null;
}

interface ThreadDetail extends Thread {
  timeline: TimelineEntry[];
  timelineTruncated: number;
}

interface WikilinkItem {
  id: string;
  name: string;
  signalCount: number;
  threadId: string | null;
}

type StatusFilter = "open" | "closed" | "all";

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// ---------------------------------------------------------------------------
// Entry point — one route registration per view, one render function here
// ---------------------------------------------------------------------------

export async function renderThreadsPage(root: HTMLElement): Promise<void> {
  root.innerHTML = "";
  root.appendChild(renderHeader());
  const page = document.createElement("div");
  page.className = "thr-page";
  page.innerHTML = `<p class="muted">Loading threads…</p>`;
  root.appendChild(page);
  await loadList(page, "open");
}

export async function renderThreadPage(
  root: HTMLElement,
  params: Record<string, string>,
): Promise<void> {
  root.innerHTML = "";
  root.appendChild(renderHeader());
  const page = document.createElement("div");
  page.className = "thr-page";
  page.innerHTML = `<p class="muted">Loading thread…</p>`;
  root.appendChild(page);
  await loadDetail(page, params.id ?? "");
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

async function loadList(page: HTMLElement, status: StatusFilter): Promise<void> {
  let threads: Thread[];
  try {
    threads = await api<Thread[]>(`/threads?status=${status}`);
  } catch {
    page.innerHTML = `<p class="ap-error" style="display:block">Could not load the threads.</p>`;
    return;
  }
  renderList(page, threads, status);
}

function renderList(page: HTMLElement, threads: Thread[], status: StatusFilter): void {
  page.innerHTML = "";

  const head = document.createElement("div");
  head.className = "thr-head";
  const heading = document.createElement("h2");
  heading.className = "thr-heading";
  heading.textContent = "Threads";
  head.appendChild(heading);

  const promote = document.createElement("button");
  promote.className = "primary thr-promote-btn";
  promote.textContent = "Follow a wikilink";
  promote.addEventListener("click", () =>
    openPromoteModal(null, () => void loadList(page, status)),
  );
  head.appendChild(promote);
  page.appendChild(head);

  const intro = document.createElement("p");
  intro.className = "muted thr-intro";
  intro.textContent =
    "A thread is a wikilink somebody decided to follow. Every signal carrying " +
    "that tag is in it, and no brief will carry more than two signals from any " +
    "one open thread.";
  page.appendChild(intro);

  const filters = document.createElement("div");
  filters.className = "thr-filters";
  (["open", "closed", "all"] as StatusFilter[]).forEach((key) => {
    const btn = document.createElement("button");
    btn.className = "thr-filter" + (key === status ? " active" : "");
    btn.textContent = key === "all" ? "All" : key === "open" ? "Open" : "Closed";
    btn.addEventListener("click", () => void loadList(page, key));
    filters.appendChild(btn);
  });
  page.appendChild(filters);

  if (!threads.length) {
    const empty = document.createElement("p");
    empty.className = "muted thr-empty";
    empty.textContent =
      status === "open"
        ? "Nothing is being followed yet. Promote a wikilink from here, from the Pulse, or from Manage Wikilinks."
        : "Nothing here.";
    page.appendChild(empty);
    return;
  }

  const list = document.createElement("div");
  list.className = "thr-list";
  threads.forEach((t) => list.appendChild(listCard(t)));
  page.appendChild(list);
}

function listCard(t: Thread): HTMLElement {
  const card = document.createElement("div");
  card.className = "thr-card";
  card.setAttribute("role", "button");
  card.tabIndex = 0;
  const go = (): void => navigate(`/threads/${encodeURIComponent(t.id)}`);
  card.addEventListener("click", go);
  card.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      go();
    }
  });

  card.innerHTML = `
    <div class="thr-card-head">
      <span class="thr-card-title">${esc(t.title)}</span>
      ${badges(t)}
    </div>
    ${t.wikilink ? `<span class="wikilink-chip">#${esc(t.wikilink)}</span>` : ""}
    ${t.framing ? `<p class="thr-card-framing">${esc(t.framing)}</p>` : ""}
    <div class="thr-card-meta">${esc(metaLine(t))}</div>`;
  return card;
}

function badges(t: Thread): string {
  const out: string[] = [];
  if (t.status === "closed") out.push(`<span class="thr-badge thr-closed">Closed</span>`);
  else if (t.dormant) out.push(`<span class="thr-badge thr-dormant">Quiet</span>`);
  return out.join("");
}

function metaLine(t: Thread): string {
  const n = t.signalCount;
  const parts = [`${n} signal${n === 1 ? "" : "s"}`];
  if (t.firstSeen) parts.push(`since ${fmtDate(t.firstSeen)}`);
  if (t.lastActivity) parts.push(`last ${fmtDate(t.lastActivity)}`);
  else parts.push("nothing collected yet");
  if (t.status === "open" && t.dormant) {
    parts.push(`quiet for over ${t.dormantAfterDays} days`);
  }
  return parts.join(" · ");
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

async function loadDetail(page: HTMLElement, id: string): Promise<void> {
  let t: ThreadDetail;
  try {
    t = await api<ThreadDetail>(`/threads/${encodeURIComponent(id)}`);
  } catch (e) {
    const missing = e instanceof ApiError && e.status === 404;
    page.innerHTML = `<p class="ap-error" style="display:block">${
      missing ? "No such thread." : "Could not load that thread."
    }</p>`;
    const back = document.createElement("a");
    back.href = "/threads";
    back.dataset.route = "";
    back.className = "thr-back";
    back.textContent = "← All threads";
    page.appendChild(back);
    return;
  }
  renderDetail(page, t);
}

function renderDetail(page: HTMLElement, t: ThreadDetail): void {
  page.innerHTML = "";

  const back = document.createElement("a");
  back.href = "/threads";
  back.dataset.route = "";
  back.className = "thr-back";
  back.textContent = "← All threads";
  page.appendChild(back);

  const head = document.createElement("div");
  head.className = "thr-detail-head";
  head.innerHTML = `
    <h2 class="thr-heading">${esc(t.title)}</h2>
    <div class="thr-detail-chips">
      ${t.wikilink ? `<span class="wikilink-chip">#${esc(t.wikilink)}</span>` : ""}
      ${badges(t)}
    </div>`;
  page.appendChild(head);

  if (t.framing) {
    const framing = document.createElement("p");
    framing.className = "thr-framing serif";
    framing.textContent = t.framing;
    page.appendChild(framing);
  }

  const meta = document.createElement("div");
  meta.className = "thr-detail-meta muted";
  meta.textContent = metaLine(t);
  page.appendChild(meta);

  if (t.status === "closed" && t.closingNote) {
    const note = document.createElement("div");
    note.className = "thr-closed-note";
    note.textContent = `Closed${t.closedAt ? ` on ${fmtDate(t.closedAt)}` : ""}: ${t.closingNote}`;
    page.appendChild(note);
  }
  if (t.mergedIntoId) {
    const cont = document.createElement("a");
    cont.className = "thr-continues";
    cont.href = `/threads/${encodeURIComponent(t.mergedIntoId)}`;
    cont.dataset.route = "";
    cont.textContent = "Continues as another thread →";
    page.appendChild(cont);
  }

  page.appendChild(detailActions(page, t));

  const err = document.createElement("p");
  err.className = "ap-error thr-error";
  err.id = "thr-detail-err";
  page.appendChild(err);

  const tl = document.createElement("div");
  tl.className = "thr-timeline";
  const h3 = document.createElement("h3");
  h3.className = "ap-wa-proposals-title";
  h3.textContent = "Timeline";
  tl.appendChild(h3);

  if (!t.timeline.length) {
    const empty = document.createElement("p");
    empty.className = "muted";
    empty.textContent = t.wikilink
      ? "Nothing carries this wikilink yet."
      : "This thread was folded into another and has no wikilink of its own.";
    tl.appendChild(empty);
  } else {
    let month = "";
    t.timeline.forEach((s) => {
      const m = (s.datePublished || "").slice(0, 7);
      if (m && m !== month) {
        month = m;
        const h = document.createElement("div");
        h.className = "thr-month";
        h.textContent = monthLabel(m);
        tl.appendChild(h);
      }
      tl.appendChild(timelineRow(s));
    });
    if (t.timelineTruncated > 0) {
      const more = document.createElement("p");
      more.className = "muted thr-truncated";
      more.textContent =
        `Showing the ${t.timeline.length} most recent of ${t.signalCount}. ` +
        `${t.timelineTruncated} older signals are not listed.`;
      tl.appendChild(more);
    }
  }
  page.appendChild(tl);
}

function monthLabel(ym: string): string {
  const [y, m] = ym.split("-");
  const names = ["January", "February", "March", "April", "May", "June", "July",
                 "August", "September", "October", "November", "December"];
  const idx = Number(m) - 1;
  return names[idx] ? `${names[idx]} ${y}` : ym;
}

function timelineRow(s: TimelineEntry): HTMLElement {
  const row = document.createElement("div");
  row.className = "thr-entry";
  // The permalink Phase 5 step 1 built. A ref is what a person can quote; the
  // uuid fallback is for rows written before refs existed.
  const href = `/signals/${encodeURIComponent(s.signalRef || s.id)}`;
  row.innerHTML = `
    <span class="thr-entry-date">${esc(fmtDate(s.datePublished))}</span>
    <div class="thr-entry-body">
      <a class="thr-entry-title" href="${href}" data-route>${esc(s.title)}</a>
      <div class="thr-entry-meta">
        ${s.signalRef ? `<span class="thr-entry-ref">${esc(s.signalRef)}</span>` : ""}
        <span>${esc(s.publisherDomain || s.source)}</span>
        ${s.horizon ? `<span class="thr-entry-horizon">${esc(s.horizon)}</span>` : ""}
      </div>
    </div>`;
  return row;
}

function detailActions(page: HTMLElement, t: ThreadDetail): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "thr-actions";
  const reload = (): void => void loadDetail(page, t.id);

  const edit = document.createElement("button");
  edit.className = "ap-action-btn";
  edit.textContent = "Edit";
  edit.addEventListener("click", () => openEditModal(t, reload));
  wrap.appendChild(edit);

  if (t.status === "open") {
    const close = document.createElement("button");
    close.className = "ap-action-btn";
    close.textContent = "Close";
    close.addEventListener("click", () => openCloseModal(t, reload));
    wrap.appendChild(close);
  } else if (!t.mergedIntoId) {
    const reopen = document.createElement("button");
    reopen.className = "ap-action-btn";
    reopen.textContent = "Reopen";
    reopen.addEventListener("click", async () => {
      try {
        await api(`/threads/${encodeURIComponent(t.id)}/reopen`, { method: "POST" });
        reload();
      } catch (e) {
        showError(page, e);
      }
    });
    wrap.appendChild(reopen);
  }

  const del = document.createElement("button");
  del.className = "ap-action-btn destructive-text";
  del.textContent = "Delete";
  del.addEventListener("click", () => openDeleteModal(t));
  wrap.appendChild(del);

  return wrap;
}

function showError(page: HTMLElement, e: unknown): void {
  const box = page.querySelector<HTMLElement>("#thr-detail-err");
  if (!box) return;
  box.textContent = e instanceof ApiError ? e.message : "That did not work.";
  box.style.display = "block";
}

// ---------------------------------------------------------------------------
// Modals — same overlay vocabulary as pages/wikilinks.ts
// ---------------------------------------------------------------------------

function overlay(title: string, bodyHtml: string): {
  ov: HTMLElement;
  box: HTMLElement;
  close: () => void;
} {
  const ov = document.createElement("div");
  ov.className = "modal-overlay";
  ov.innerHTML = `
    <div class="modal-box">
      <h3>${esc(title)}</h3>
      ${bodyHtml}
      <p class="ap-error thr-modal-err"></p>
      <div class="modal-footer">
        <button class="modal-cancel">Cancel</button>
        <button class="primary modal-confirm">Save</button>
      </div>
    </div>`;
  document.body.appendChild(ov);
  const close = (): void => ov.remove();
  ov.querySelector<HTMLButtonElement>(".modal-cancel")!.addEventListener("click", close);
  ov.addEventListener("click", (e) => {
    if (e.target === ov) close();
  });
  return { ov, box: ov.querySelector<HTMLElement>(".modal-box")!, close };
}

function modalError(ov: HTMLElement, e: unknown): void {
  const box = ov.querySelector<HTMLElement>(".thr-modal-err")!;
  box.textContent = e instanceof ApiError ? e.message : "That did not work.";
  box.style.display = "block";
}

/**
 * Promote a wikilink. `preset` skips the picker when the caller already knows
 * which wikilink — the Pulse card and the Wikilinks table both do.
 */
export function openPromoteModal(
  preset: { id: string; name: string } | null,
  done: (threadId: string) => void,
): void {
  const picker = preset
    ? `<p class="thr-modal-preset"><span class="wikilink-chip">#${esc(preset.name)}</span></p>`
    : `<label class="thr-label">Wikilink
         <select id="thr-wl"><option value="">Loading…</option></select>
       </label>`;
  const { ov, close } = overlay(
    "Follow a wikilink",
    `${picker}
     <label class="thr-label">Title
       <input id="thr-title" type="text" placeholder="What is this story called?" />
     </label>
     <label class="thr-label">What are you watching for? <span class="muted">(optional)</span>
       <textarea id="thr-framing" rows="3"></textarea>
     </label>`,
  );
  ov.querySelector<HTMLButtonElement>(".modal-confirm")!.textContent = "Follow";

  const titleInput = ov.querySelector<HTMLInputElement>("#thr-title")!;
  if (preset) titleInput.value = preset.name;

  if (!preset) {
    const select = ov.querySelector<HTMLSelectElement>("#thr-wl")!;
    void (async () => {
      try {
        const all = await api<WikilinkItem[]>("/wikilinks");
        // Already-promoted wikilinks are omitted: offering one would only ever
        // produce the 409 the server correctly returns.
        const free = all.filter((w) => !w.threadId);
        select.innerHTML = free.length
          ? free
              .map(
                (w) =>
                  `<option value="${esc(w.id)}" data-name="${esc(w.name)}">#${esc(w.name)} (${w.signalCount})</option>`,
              )
              .join("")
          : `<option value="">Every wikilink is already a thread</option>`;
        if (free.length && !titleInput.value) titleInput.value = free[0]!.name;
        select.addEventListener("change", () => {
          const opt = select.selectedOptions[0];
          if (opt?.dataset.name) titleInput.value = opt.dataset.name;
        });
      } catch {
        select.innerHTML = `<option value="">Could not load wikilinks</option>`;
      }
    })();
  }

  ov.querySelector<HTMLButtonElement>(".modal-confirm")!.addEventListener("click", async () => {
    const wikilinkId =
      preset?.id ?? ov.querySelector<HTMLSelectElement>("#thr-wl")!.value;
    if (!wikilinkId) {
      modalError(ov, new ApiError(400, "Pick a wikilink first."));
      return;
    }
    try {
      const created = await api<Thread>("/threads", {
        method: "POST",
        body: {
          wikilink_id: wikilinkId,
          title: titleInput.value.trim() || undefined,
          framing: ov.querySelector<HTMLTextAreaElement>("#thr-framing")!.value.trim() || undefined,
        },
      });
      close();
      done(created.id);
    } catch (e) {
      modalError(ov, e);
    }
  });
}

function openEditModal(t: Thread, done: () => void): void {
  const { ov, close } = overlay(
    "Edit thread",
    `<label class="thr-label">Title
       <input id="thr-title" type="text" value="${esc(t.title)}" />
     </label>
     <label class="thr-label">What are you watching for?
       <textarea id="thr-framing" rows="3">${esc(t.framing ?? "")}</textarea>
     </label>`,
  );
  ov.querySelector<HTMLButtonElement>(".modal-confirm")!.addEventListener("click", async () => {
    try {
      await api(`/threads/${encodeURIComponent(t.id)}`, {
        method: "PATCH",
        body: {
          title: ov.querySelector<HTMLInputElement>("#thr-title")!.value,
          framing: ov.querySelector<HTMLTextAreaElement>("#thr-framing")!.value,
        },
      });
      close();
      done();
    } catch (e) {
      modalError(ov, e);
    }
  });
}

function openCloseModal(t: Thread, done: () => void): void {
  const { ov, close } = overlay(
    "Close this thread",
    `<p class="muted">Nothing is deleted and no signal is untagged. The thread stops
      counting against the two-per-thread cap on briefs, and leaves the open list.</p>
     <label class="thr-label">What happened? <span class="muted">(optional)</span>
       <textarea id="thr-note" rows="3"></textarea>
     </label>`,
  );
  ov.querySelector<HTMLButtonElement>(".modal-confirm")!.textContent = "Close thread";
  ov.querySelector<HTMLButtonElement>(".modal-confirm")!.addEventListener("click", async () => {
    try {
      await api(`/threads/${encodeURIComponent(t.id)}/close`, {
        method: "POST",
        body: { note: ov.querySelector<HTMLTextAreaElement>("#thr-note")!.value },
      });
      close();
      done();
    } catch (e) {
      modalError(ov, e);
    }
  });
}

function openDeleteModal(t: Thread): void {
  const { ov, close } = overlay(
    "Delete this thread",
    `<p>This forgets the title, the framing and the closing note. The wikilink and
      every signal on it are untouched.</p>
     <p class="muted">Closing it instead keeps the record.</p>`,
  );
  const confirm = ov.querySelector<HTMLButtonElement>(".modal-confirm")!;
  confirm.textContent = "Delete";
  confirm.classList.add("destructive");
  confirm.addEventListener("click", async () => {
    try {
      await api(`/threads/${encodeURIComponent(t.id)}`, { method: "DELETE" });
      close();
      navigate("/threads");
    } catch (e) {
      modalError(ov, e);
    }
  });
}
